import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { requireAuth, requireDb, requireFunctions } from './firebase'
import {
  defaultSettings,
  emptySnapshot,
  type CloudAccountSnapshot,
  type CloudCategory,
  type CloudLayout,
  type CloudLayoutSection,
  type CloudShoppingItem,
  type CloudShoppingList,
  type CloudStore,
  type CloudUserSettings,
  type Language,
  type ListInvite,
} from './models'

const jsonParse = <T>(value: unknown): T | null => {
  if (typeof value !== 'string' || !value.trim()) return null
  try { return JSON.parse(value) as T } catch { return null }
}
const now = () => Date.now()
const id = () => crypto.randomUUID()

const sha256 = async (text: string): Promise<string> => {
  const bytes = new TextEncoder().encode(text)
  const hash = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(hash)).map((value) => value.toString(16).padStart(2, '0')).join('')
}

const normalizeEmail = (email: string) => email.trim().toLowerCase()
const normalize = (value: string) => value.trim().toLocaleLowerCase('sv-SE')

/**
 * Firestore applies local mutations to its persistent cache immediately, but the returned Promise
 * may wait for a backend acknowledgement while the browser is offline. Shopping-list mutations
 * must never block the UI on that acknowledgement. Queue the write and surface eventual failures
 * in the console while listeners keep the UI backed by the local cache.
 */
const queueOfflineWrite = (operation: Promise<unknown>, label: string) => {
  void operation.catch((error) => console.error(`Shelfway ${label} sync failed`, error))
}

type LocalPreference = { categoryId: string; positiveWeight: number; negativeWeight: number; storeId: string | null; lastUsedAt: number }
type LocalPreferenceStore = Record<string, LocalPreference[]>

const preferenceKey = (uid: string) => `shelfway:recommendation-preferences:${uid}`
const productKey = (title: string) => normalize(title).normalize('NFC')
const readPreferenceStore = (uid: string): LocalPreferenceStore => {
  try { return JSON.parse(localStorage.getItem(preferenceKey(uid)) || '{}') as LocalPreferenceStore } catch { return {} }
}
const writePreferenceStore = (uid: string, value: LocalPreferenceStore) => localStorage.setItem(preferenceKey(uid), JSON.stringify(value))

function recordLocalEvidence(uid: string, title: string, categoryId: string, storeId: string | null | undefined, weight: number) {
  const all = readPreferenceStore(uid)
  const key = productKey(title)
  const entries = all[key] ?? []
  const normalizedStore = storeId || null
  let preference = entries.find((entry) => entry.categoryId === categoryId && entry.storeId === normalizedStore)
  if (!preference) {
    preference = { categoryId, positiveWeight: 0, negativeWeight: 0, storeId: normalizedStore, lastUsedAt: now() }
    entries.push(preference)
  }
  if (weight > 0) preference.positiveWeight += weight
  if (weight < 0) preference.negativeWeight += -weight
  preference.lastUsedAt = now()
  all[key] = entries
  writePreferenceStore(uid, all)
}

function recommendLocalCategory(uid: string, title: string, storeId?: string | null) {
  const entries = readPreferenceStore(uid)[productKey(title)] ?? []
  const scores = new Map<string, number>()
  for (const preference of entries) {
    const delta = preference.positiveWeight - preference.negativeWeight
    const weight = storeId && preference.storeId === storeId ? 5 : 4
    scores.set(preference.categoryId, (scores.get(preference.categoryId) ?? 0) + delta * weight)
  }
  const ranked = [...scores.entries()].filter(([, score]) => score > 0).sort((a, b) => b[1] - a[1])
  if (!ranked.length) return { categoryId: 'cat_other', source: 'fallback', confidence: 'none' }
  const [categoryId, score] = ranked[0]
  const nextScore = ranked[1]?.[1]
  const confidence = score >= 12 ? 'strong' : nextScore != null && score - nextScore < 2 ? 'weak' : score >= 4 ? 'medium' : 'weak'
  return { categoryId, source: 'personal', confidence }
}

export async function emailSignIn(email: string, password: string) {
  const result = await signInWithEmailAndPassword(requireAuth(), email.trim(), password)
  await ensureAccountDirectory(result.user)
  return result.user
}

export async function emailSignUp(email: string, password: string) {
  const result = await createUserWithEmailAndPassword(requireAuth(), email.trim(), password)
  await ensureAccountDirectory(result.user)
  await mergeSettings(result.user.uid, defaultSettings(navigator.language.toLowerCase().startsWith('sv') ? 'sv' : 'en'))
  return result.user
}

export async function googleSignIn() {
  const result = await signInWithPopup(requireAuth(), new GoogleAuthProvider())
  await ensureAccountDirectory(result.user)
  return result.user
}

export const accountSignOut = () => signOut(requireAuth())

export async function ensureAccountDirectory(user: User) {
  if (!user.email) return
  const email = normalizeEmail(user.email)
  const hash = await sha256(email)
  await setDoc(doc(requireDb(), 'email_directory', hash), {
    uid: user.uid,
    email,
    updated_at: now(),
  }, { merge: true })
}

export async function loadSnapshot(uid: string): Promise<CloudAccountSnapshot> {
  const profile = await getDoc(doc(requireDb(), 'profiles', uid))
  const base = jsonParse<CloudAccountSnapshot>(profile.data()?.aisle_sync_payload) ?? emptySnapshot()
  return {
    ...emptySnapshot(),
    ...base,
    categories: base.categories ?? [],
    stores: base.stores ?? [],
    layouts: base.layouts ?? [],
    lists: base.lists ?? [],
    archivedStoreIds: base.archivedStoreIds ?? [],
    archivedLayoutIds: base.archivedLayoutIds ?? [],
  }
}

export async function saveSnapshot(uid: string, snapshot: CloudAccountSnapshot, email?: string | null) {
  const profileOnly = { ...snapshot, stores: [], layouts: [], lists: [], updatedAt: now() }
  await setDoc(doc(requireDb(), 'profiles', uid), {
    email: email ?? null,
    aisle_sync_version: profileOnly.version || 1,
    aisle_sync_updated_at: profileOnly.updatedAt,
    aisle_sync_payload: JSON.stringify(profileOnly),
  }, { merge: true })
}

export async function mergeSettings(uid: string, settings: CloudUserSettings, email?: string | null) {
  const snapshot = await loadSnapshot(uid)
  await saveSnapshot(uid, { ...snapshot, settings, updatedAt: now() }, email)
}

const listFromDoc = (snapshot: QueryDocumentSnapshot<DocumentData>): CloudShoppingList | null => {
  const payload = jsonParse<CloudShoppingList>(snapshot.data().payload)
  if (!payload) return null
  const members = (snapshot.data().members ?? {}) as Record<string, string>
  return {
    ...payload,
    items: [],
    ownerUserId: snapshot.data().owner_id,
    memberRole: members[requireAuth().currentUser?.uid ?? ''] ?? undefined,
  }
}

export function subscribeAccessibleLists(uid: string, callback: (lists: CloudShoppingList[]) => void): Unsubscribe {
  const db = requireDb()
  const owned = new Map<string, CloudShoppingList>()
  const shared = new Map<string, CloudShoppingList>()
  const items = new Map<string, CloudShoppingItem[]>()
  const itemUnsubs = new Map<string, Unsubscribe>()

  const combined = () => new Map<string, CloudShoppingList>([...owned, ...shared])
  const emit = () => callback(
    [...combined().values()]
      .map((list) => ({ ...list, items: items.get(list.id) ?? [] }))
      .filter((list) => list.status !== 'deleted')
      .sort((a, b) => b.updatedAt - a.updatedAt),
  )

  const syncItemListeners = () => {
    const metadata = combined()
    for (const [listId, unsubscribe] of itemUnsubs.entries()) {
      if (!metadata.has(listId)) {
        unsubscribe()
        itemUnsubs.delete(listId)
        items.delete(listId)
      }
    }
    for (const listId of metadata.keys()) {
      if (itemUnsubs.has(listId)) continue
      const unsubscribe = onSnapshot(
        collection(db, 'lists', listId, 'items'),
        (snapshot) => {
          const next = snapshot.docs
            .map((itemDoc) => jsonParse<CloudShoppingItem>(itemDoc.data().payload))
            .filter((item): item is CloudShoppingItem => Boolean(item && !item.deletedAt))
            .sort((a, b) => a.manualSortPosition - b.manualSortPosition || a.createdAt - b.createdAt)
          items.set(listId, next)
          emit()
        },
        (error) => console.error(`Shelfway item listener failed for ${listId}`, error),
      )
      itemUnsubs.set(listId, unsubscribe)
    }
    emit()
  }

  const replaceMap = (target: Map<string, CloudShoppingList>, docs: QueryDocumentSnapshot<DocumentData>[]) => {
    target.clear()
    docs.forEach((snapshot) => {
      const parsed = listFromDoc(snapshot)
      if (parsed) target.set(parsed.id, parsed)
    })
    syncItemListeners()
  }

  const ownedUnsub = onSnapshot(
    query(collection(db, 'lists'), where('owner_id', '==', uid)),
    (snapshot) => replaceMap(owned, snapshot.docs),
    (error) => console.error('Shelfway owned-list listener failed', error),
  )
  const sharedUnsub = onSnapshot(
    query(collection(db, 'lists'), where('member_uids', 'array-contains', uid)),
    (snapshot) => replaceMap(shared, snapshot.docs),
    (error) => console.error('Shelfway shared-list listener failed', error),
  )

  return () => {
    ownedUnsub()
    sharedUnsub()
    itemUnsubs.forEach((unsubscribe) => unsubscribe())
  }
}

export async function createList(uid: string, name: string): Promise<CloudShoppingList> {
  const timestamp = now()
  const list: CloudShoppingList = {
    id: id(), name: name.trim() || 'Shopping list', status: 'active', createdAt: timestamp, updatedAt: timestamp, items: [],
  }
  queueOfflineWrite(setDoc(doc(requireDb(), 'lists', list.id), {
    owner_id: uid,
    name: list.name,
    store_id: null,
    layout_id: null,
    status: list.status,
    created_at: list.createdAt,
    updated_at: list.updatedAt,
    completed_at: null,
    members: { [uid]: 'owner' },
    member_uids: [],
    payload: JSON.stringify(list),
  }), 'create list')
  return list
}

export async function updateListMetadata(list: CloudShoppingList) {
  const next = { ...list, updatedAt: now(), items: [] }
  queueOfflineWrite(setDoc(doc(requireDb(), 'lists', list.id), {
    name: next.name,
    store_id: next.storeId ?? null,
    layout_id: next.layoutId ?? null,
    status: next.status,
    updated_at: next.updatedAt,
    completed_at: next.completedAt ?? null,
    payload: JSON.stringify(next),
  }, { merge: true }), 'update list metadata')
}

export async function deleteList(listId: string) {
  const db = requireDb()
  const itemDocs = await getDocs(collection(db, 'lists', listId, 'items'))
  const batch = writeBatch(db)
  itemDocs.docs.forEach((snapshot) => batch.delete(snapshot.ref))
  batch.delete(doc(db, 'lists', listId))
  await batch.commit()
}

export async function addItems(list: CloudShoppingList, rawLines: string[]) {
  const db = requireDb()
  const clean = rawLines.map((line) => line.trim()).filter(Boolean)
  const start = Math.max(-1, ...list.items.map((item) => item.manualSortPosition)) + 1
  const timestamp = now()
  const uid = requireAuth().currentUser?.uid
  if (!uid) throw new Error('Sign in first.')
  const batch = writeBatch(db)
  clean.forEach((line, index) => {
    const recommendation = recommendLocalCategory(uid, line, list.storeId)
    const item: CloudShoppingItem = {
      id: id(), displayTitle: line, originalInput: line, isChecked: false,
      categoryId: recommendation.categoryId, recommendationSource: recommendation.source, recommendationConfidence: recommendation.confidence,
      manualSortPosition: start + index, createdAt: timestamp + index, updatedAt: timestamp + index,
    }
    recordLocalEvidence(uid, line, recommendation.categoryId, list.storeId, 1)
    batch.set(doc(db, 'lists', list.id, 'items', item.id), {
      updated_at: item.updatedAt,
      deleted_at: null,
      payload: JSON.stringify(item),
    }, { merge: true })
  })
  queueOfflineWrite(batch.commit(), 'add list items')
  if (list.ownerUserId === uid) await updateListMetadata({ ...list, updatedAt: timestamp })
}

export async function updateItem(listId: string, item: CloudShoppingItem, patch: Partial<CloudShoppingItem>) {
  const next = { ...item, ...patch, updatedAt: now() }
  queueOfflineWrite(setDoc(doc(requireDb(), 'lists', listId, 'items', item.id), {
    updated_at: next.updatedAt,
    deleted_at: next.deletedAt ?? null,
    payload: JSON.stringify(next),
  }, { merge: true }), 'update list item')
}

export async function removeItem(listId: string, item: CloudShoppingItem) {
  await updateItem(listId, item, { deletedAt: now() })
}

export async function correctItemCategory(list: CloudShoppingList, item: CloudShoppingItem, categoryId: string) {
  const uid = requireAuth().currentUser?.uid
  if (!uid) throw new Error('Sign in first.')
  if (item.categoryId && item.categoryId !== categoryId) recordLocalEvidence(uid, item.displayTitle, item.categoryId, list.storeId, -3)
  recordLocalEvidence(uid, item.displayTitle, categoryId, list.storeId, 3)
  await updateItem(list.id, item, { categoryId, recommendationSource: 'manual', recommendationConfidence: 'strong' })
}

export async function approveItemCategory(list: CloudShoppingList, item: CloudShoppingItem) {
  const uid = requireAuth().currentUser?.uid
  if (!uid || !item.categoryId) return
  recordLocalEvidence(uid, item.displayTitle, item.categoryId, list.storeId, 1)
  await updateItem(list.id, item, { recommendationSource: 'confirmed', recommendationConfidence: 'strong' })
}

export async function rejectItemCategory(list: CloudShoppingList, item: CloudShoppingItem, alternatives: string[]) {
  const uid = requireAuth().currentUser?.uid
  if (!uid || !item.categoryId) return
  recordLocalEvidence(uid, item.displayTitle, item.categoryId, list.storeId, -2)
  const next = alternatives.find((candidate) => candidate !== item.categoryId) ?? 'cat_other'
  await updateItem(list.id, item, { categoryId: next, recommendationSource: 'next_candidate', recommendationConfidence: 'weak' })
}

export async function importListItems(list: CloudShoppingList, text: string) {
  const parsed = JSON.parse(text) as { items?: Array<Record<string, unknown>> }
  if (!Array.isArray(parsed.items)) throw new Error('JSON must contain an items array.')
  const lines = parsed.items.map((raw) => String(raw.title ?? raw.displayTitle ?? '').trim()).filter(Boolean)
  if (!lines.length) throw new Error('No importable items were found.')
  await addItems(list, lines)
}

export function exportListJson(list: CloudShoppingList) {
  return JSON.stringify({
    schema: 'shelfway.shopping-list',
    version: 1,
    list: { id: list.id, name: list.name, store_id: list.storeId ?? null, layout_id: list.layoutId ?? null },
    items: list.items.map((item) => ({
      title: item.displayTitle,
      ...(item.quantityText ? { suffix: item.quantityText } : {}),
      checked: item.isChecked,
      category_id: item.categoryId ?? null,
    })),
  }, null, 2)
}

export async function listMyStores(uid: string): Promise<CloudStore[]> {
  const snapshots = await getDocs(query(collection(requireDb(), 'stores'), where('created_by', '==', uid)))
  return snapshots.docs.map((snapshot) => jsonParse<CloudStore>(snapshot.data().payload)).filter((value): value is CloudStore => Boolean(value))
}

export async function listAllStores(): Promise<CloudStore[]> {
  const snapshots = await getDocs(collection(requireDb(), 'stores'))
  return snapshots.docs.map((snapshot) => jsonParse<CloudStore>(snapshot.data().payload)).filter((value): value is CloudStore => Boolean(value))
}

export async function createStore(uid: string, name: string, city: string): Promise<CloudStore> {
  const timestamp = now()
  const store: CloudStore = {
    id: id(), name: name.trim(), city: city.trim() || null, ownerUserId: uid, createdAt: timestamp, updatedAt: timestamp,
  }
  await setDoc(doc(requireDb(), 'stores', store.id), {
    created_by: uid,
    name: store.name,
    name_normalized: normalize(store.name),
    city: store.city ?? null,
    city_normalized: normalize(store.city ?? ''),
    updated_at: timestamp,
    payload: JSON.stringify(store),
  })
  return store
}

export async function listLayoutsForStore(storeId: string): Promise<CloudLayout[]> {
  const snapshots = await getDocs(query(collection(requireDb(), 'store_layouts'), where('store_id', '==', storeId)))
  return snapshots.docs.map((snapshot) => jsonParse<CloudLayout>(snapshot.data().payload)).filter((value): value is CloudLayout => Boolean(value))
}

export async function loadLayout(layoutId?: string | null): Promise<CloudLayout | null> {
  if (!layoutId) return null
  const snapshot = await getDoc(doc(requireDb(), 'store_layouts', layoutId))
  return snapshot.exists() ? jsonParse<CloudLayout>(snapshot.data().payload) : null
}

export async function createLayout(uid: string, storeId: string, name: string, categoryNames: string[], language: Language): Promise<CloudLayout> {
  const timestamp = now()
  const sections: CloudLayoutSection[] = categoryNames.map((categoryName, index) => ({
    id: id(),
    categoryId: `user_${id()}`,
    displayOrder: index,
    createdAt: timestamp,
    updatedAt: timestamp,
    categoryNames: { [language]: categoryName.trim() },
  }))
  const layout: CloudLayout = {
    id: id(), storeId, ownerUserId: uid, name: name.trim(), status: 'active', createdAt: timestamp, updatedAt: timestamp, sections,
  }
  await setDoc(doc(requireDb(), 'store_layouts', layout.id), {
    created_by: uid,
    store_id: storeId,
    name: layout.name,
    updated_at: timestamp,
    payload: JSON.stringify(layout),
  })

  const snapshot = await loadSnapshot(uid)
  const newCategories: CloudCategory[] = sections.map((section) => ({
    id: section.categoryId, scope: 'user', createdByUserId: uid, createdAt: timestamp, updatedAt: timestamp,
    translations: [{ language, name: section.categoryNames[language], source: 'user' }], aliases: [],
  }))
  const existingIds = new Set(snapshot.categories.map((category) => category.id))
  await saveSnapshot(uid, { ...snapshot, categories: [...snapshot.categories, ...newCategories.filter((category) => !existingIds.has(category.id))], updatedAt: timestamp }, requireAuth().currentUser?.email)
  return layout
}

export async function saveLayout(layout: CloudLayout) {
  const next: CloudLayout = { ...layout, updatedAt: now(), sections: layout.sections.map((section, index) => ({ ...section, displayOrder: index, updatedAt: now() })) }
  await setDoc(doc(requireDb(), 'store_layouts', layout.id), {
    name: next.name,
    store_id: next.storeId,
    updated_at: next.updatedAt,
    payload: JSON.stringify(next),
  }, { merge: true })
}

export async function inviteToList(list: CloudShoppingList, recipientEmail: string) {
  const current = requireAuth().currentUser
  if (!current) throw new Error('Sign in first.')
  const email = normalizeEmail(recipientEmail)
  if (email === normalizeEmail(current.email ?? '')) throw new Error('You cannot invite yourself.')
  const directory = await getDoc(doc(requireDb(), 'email_directory', await sha256(email)))
  const recipientUid = directory.data()?.uid as string | undefined
  if (!recipientUid) throw new Error('No Shelfway account was found for that email address.')
  const inviteId = `${list.id}_${recipientUid}`
  const timestamp = now()
  await setDoc(doc(requireDb(), 'list_invites', inviteId), {
    id: inviteId,
    list_id: list.id,
    list_name: list.name,
    owner_id: current.uid,
    owner_email: current.email ?? null,
    recipient_uid: recipientUid,
    recipient_email: directory.data()?.email ?? email,
    status: 'pending',
    created_at: timestamp,
    updated_at: timestamp,
  })
}

const inviteFromDoc = (snapshot: QueryDocumentSnapshot<DocumentData>): ListInvite => ({
  id: snapshot.id,
  listId: snapshot.data().list_id,
  listName: snapshot.data().list_name,
  ownerUserId: snapshot.data().owner_id,
  ownerEmail: snapshot.data().owner_email,
  recipientUserId: snapshot.data().recipient_uid,
  recipientEmail: snapshot.data().recipient_email,
  status: snapshot.data().status,
  createdAt: snapshot.data().created_at,
  updatedAt: snapshot.data().updated_at,
})

export function subscribeInvites(uid: string, callback: (invites: ListInvite[]) => void): Unsubscribe {
  return onSnapshot(query(collection(requireDb(), 'list_invites'), where('recipient_uid', '==', uid)), (snapshot) => {
    callback(snapshot.docs.map(inviteFromDoc).filter((invite) => invite.status === 'pending').sort((a, b) => b.createdAt - a.createdAt))
  })
}

export async function respondToInvite(invite: ListInvite, accept: boolean) {
  const db = requireDb()
  const status = accept ? 'accepted' : 'declined'
  const timestamp = now()
  await updateDoc(doc(db, 'list_invites', invite.id), { status, updated_at: timestamp })
  if (accept) {
    const uid = requireAuth().currentUser?.uid
    if (!uid) throw new Error('Sign in first.')
    await updateDoc(doc(db, 'lists', invite.listId), {
      [`members.${uid}`]: 'editor',
      member_uids: arrayUnion(uid),
      updated_at: timestamp,
    })
  }
}

export async function leaveSharedList(list: CloudShoppingList) {
  const uid = requireAuth().currentUser?.uid
  if (!uid) return
  await updateDoc(doc(requireDb(), 'lists', list.id), {
    [`members.${uid}`]: deleteField(),
    member_uids: arrayRemove(uid),
    updated_at: now(),
  })
}

export async function sendFeedback(category: string, title: string, message: string, locale: string) {
  const uid = requireAuth().currentUser?.uid
  if (!uid) throw new Error('Sign in first.')
  const submissionId = id().replaceAll('-', '')
  try {
    const callable = httpsCallable(requireFunctions(), 'submitAppFeedback')
    await callable({ submissionId, category, title, message, appVersion: 'pwa-0.1.0', platform: 'web-pwa', locale, screen: 'settings' })
  } catch {
    await setDoc(doc(requireDb(), 'app_feedback', submissionId), {
      user_id: uid, category, title, message, status: 'new', app_version: 'pwa-0.1.0', platform: 'web-pwa', locale, screen: 'settings',
      created_at: serverTimestamp(), updated_at: serverTimestamp(),
    })
  }
}

async function deleteQueryDocs(path: string, field: string, value: string) {
  const snapshots = await getDocs(query(collection(requireDb(), path), where(field, '==', value)))
  await Promise.all(snapshots.docs.map((snapshot) => deleteDoc(snapshot.ref)))
}

export async function deleteCurrentAccount() {
  const auth = requireAuth()
  const user = auth.currentUser
  if (!user) throw new Error('No signed-in account.')
  const db = requireDb()
  const uid = user.uid

  const ownedLists = await getDocs(query(collection(db, 'lists'), where('owner_id', '==', uid)))
  for (const listDoc of ownedLists.docs) {
    const itemDocs = await getDocs(collection(listDoc.ref, 'items'))
    await Promise.all(itemDocs.docs.map((itemDoc) => deleteDoc(itemDoc.ref)))
    const listInvites = await getDocs(query(collection(db, 'list_invites'), where('list_id', '==', listDoc.id)))
    await Promise.all(listInvites.docs.map((inviteDoc) => deleteDoc(inviteDoc.ref)))
    await deleteDoc(listDoc.ref)
  }

  const sharedLists = await getDocs(query(collection(db, 'lists'), where('member_uids', 'array-contains', uid)))
  for (const listDoc of sharedLists.docs) {
    if (listDoc.data().owner_id === uid) continue
    await updateDoc(listDoc.ref, {
      [`members.${uid}`]: deleteField(), member_uids: arrayRemove(uid), updated_at: now(),
    })
  }

  await deleteQueryDocs('list_invites', 'owner_id', uid)
  await deleteQueryDocs('list_invites', 'recipient_uid', uid)

  const layouts = await getDocs(query(collection(db, 'store_layouts'), where('created_by', '==', uid)))
  for (const layoutDoc of layouts.docs) {
    const sections = await getDocs(collection(layoutDoc.ref, 'layout_sections'))
    await Promise.all(sections.docs.map((sectionDoc) => deleteDoc(sectionDoc.ref)))
    await deleteDoc(layoutDoc.ref)
  }

  await deleteQueryDocs('stores', 'created_by', uid)
  await deleteQueryDocs('product_concepts', 'created_by', uid)
  await deleteQueryDocs('evidence_events', 'user_id', uid)
  await deleteDoc(doc(db, 'profiles', uid)).catch(() => undefined)

  if (user.email) {
    await deleteDoc(doc(db, 'email_directory', await sha256(normalizeEmail(user.email)))).catch(() => undefined)
  }
  const devices = await getDocs(collection(db, 'user_devices', uid, 'installations'))
  await Promise.all(devices.docs.map((deviceDoc) => deleteDoc(deviceDoc.ref)))
  await deleteUser(user)
}